/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { EventChannel } from 'redux-saga';
import { all, put, select, take } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { gameLogger, selectUserId } from '~/app';
import { setConnected, setCurrentState, StartDebugParameters } from '~/game';
import {
  setEventLog,
  setLastCard,
  setLastCardHorseIndex,
  setLastTurnDicePlayerId,
  setLastTurnDices,
  setLastTurnDicesCur,
} from '~/overlay';
import { GameState, GameType } from '~/server/src/dtos';
import { Computer, Horse, NewGameRequest, Player } from '~/server/src/dtos/controller';
import { Controller, View } from '~/server/src/interfaces';
import Turfmaster from '~/server/src/tmmodel';

import { SetupChannelEvent } from '../channelEvent';
import { setLastActiveGameId } from '../reconnectStorage';
import { actions as setupActions } from '../state';

const defaultHorseCount = 2;
const defaultPlayerCount = 3;

function* sagaSetupDebug(
  view: View,
  controller: Controller,
  channel: EventChannel<SetupChannelEvent>,
  gameParams: StartDebugParameters = {
    horseCount: defaultHorseCount,
    playerCount: defaultPlayerCount,
    gameName: 'debug',
  },
) {
  const { playerCount = defaultPlayerCount, horseCount = defaultHorseCount, gameName } = gameParams;
  const horseAmount = playerCount * horseCount;
  const player: Player = {
    horses: [...Array(horseCount).keys()].map<Horse>((horseId) => ({
      horseid: horseId.toString(),
    })),
  };
  const computers: Computer[] = Array.from({ length: playerCount - 1 }, (_v, k) => k).map(
    (playerIndex) => ({
      userid: `bot_${uuid()}`, // Use UUID-based bot user IDs with 'bot_' prefix for consistency
      horses: Array.from({ length: horseCount }, (_v, k) => k).map((horseIndex) => ({
        horseid: `bot_${playerIndex * horseCount + horseIndex}`,
      })),
    }),
  );

  const newGameRequest: NewGameRequest = {
    gameName: gameName ?? 'debug',
    horseamount: horseAmount,
    player,
    computers,
    view,
  };

  yield put(setupActions.setRestoreState('loading'));

  // send NEWGAME as a receiver callback for controller messages
  controller.receiveMsg(Turfmaster.stateMachine.NEWGAME, newGameRequest);

  const { state, params } = yield take(channel);

  if (state === Turfmaster.stateMachine.NEWGAME) {
    const game = controller.getGame();
    const board = controller.getBoard();
    const eventLog = controller.getEventLog();
    if (params?.error || !game || !board) {
      gameLogger.warn(
        { error: params?.error, hasGame: !!game, hasBoard: !!board, gameName },
        'sagaSetupDebug: NEWGAME failed',
      );
      yield put(setupActions.setRestoreState('error'));
      return;
    }
    yield all([
      put(setupActions.setGameId(params.gameid)),
      put(setupActions.setGameType(GameType.Free)),
      put(setupActions.setPlayerId(params.playerid)),
      put(setCurrentState(state)),
      put(setEventLog(eventLog)),
      put(setConnected(true)),
      put(setupActions.setRestoreState('none')),
    ]);
    setLastActiveGameId(params.gameid);
    return;
  } else if (state === Turfmaster.stateMachine.RESTOREGAME) {
    gameLogger.debug({ state, params }, 'controller => client: RESTOREGAME');
    const restorableGameState: GameState | undefined = params?.game;

    if (!restorableGameState) {
      gameLogger.warn({ gameName }, 'sagaSetupDebug: RESTOREGAME missing game payload');
      yield put(setupActions.setRestoreState('error'));
      return;
    }

    controller.receiveMsg(Turfmaster.stateMachine.REJOINGAME, { view });
    const { state: rejoinState, params: rejoinParams } = yield take(channel);
    yield put(setCurrentState(rejoinState));

    const game = controller.getGame();
    const board = controller.getBoard();
    const connectionUserId: string | null = yield select(selectUserId);
    const matchedConnectionPlayerId =
      typeof connectionUserId === 'string' && Array.isArray(game?.players)
        ? game.players.findIndex((player) => player?.userid === connectionUserId)
        : -1;
    const controllerPlayerId =
      typeof controller.getPlayerId === 'function' ? Number(controller.getPlayerId()) : -1;
    const resolvedPlayerId =
      typeof rejoinParams?.playerid === 'number' && Number.isFinite(rejoinParams.playerid)
        ? rejoinParams.playerid
        : matchedConnectionPlayerId >= 0
          ? matchedConnectionPlayerId
        : Number.isFinite(controllerPlayerId) && controllerPlayerId >= 0
          ? controllerPlayerId
          : -1;

    if (!game || !board || resolvedPlayerId < 0) {
      gameLogger.warn(
        { rejoinState, hasGame: !!game, hasBoard: !!board, resolvedPlayerId, gameName },
        'sagaSetupDebug: REJOINGAME failed after RESTOREGAME',
      );
      yield put(setupActions.setRestoreState('error'));
      return;
    }

    gameLogger.debug({ state: rejoinState, params: rejoinParams, game, board }, 'controller => client: REJOINGAME');

    const lastTurnDices = game.turndices.length > 0 ? game.turndices : [-1, -1];
    const previousTurnHorseIndex =
      game.turntype === Turfmaster.turntypes.CARDS && game.turnorder.length > 0
        ? game.turnorder[(game.turnpos - 1 + game.turnorder.length) % game.turnorder.length]
        : -1;

    // rejoin successful
    yield all([
      put(setupActions.setGameId(restorableGameState.gameId)),
      put(setupActions.setGameType(restorableGameState.gameType)),
      put(setupActions.setPlayerId(resolvedPlayerId)),
      put(setCurrentState(game.currentState)),
      put(setConnected(true)),
      put(setupActions.setRestoreState('success')),

      // try to reconstruct lastTurnDice player and array
      put(setLastTurnDicePlayerId(game.turndiceplayerid)),
      put(setLastTurnDices(lastTurnDices)),
      put(setLastTurnDicesCur(game.turndicescur)),

      // Reconstruct last card horse index safely; card value itself is unknown after restore.
      put(setLastCardHorseIndex(previousTurnHorseIndex ?? -1)),
      put(setLastCard(-1)),
    ]);
    setLastActiveGameId(restorableGameState.gameId);

    // if state is selectmove while restoring, send SELECTCARDDICE with -1 to get moves via params again
    if (game.currentState === Turfmaster.stateMachine.SELECTMOVE) {
      const horseid = game.turnorder[game.turnpos];
      controller.receiveMsg(Turfmaster.stateMachine.SELECTCARDDICE, {
        playerid: resolvedPlayerId,
        horseid,
        carddiceid: -1,
      });
    }
  }
}

export default sagaSetupDebug;
